import {announce} from '@github-ui/aria-live'
import {isLoggedIn} from '@github-ui/client-env'
import {useQueryClient} from '@github-ui/react-query'
import {useAlive} from '@github-ui/use-alive'
import {BellIcon, BellSlashIcon} from '@primer/octicons-react'
import {ActionList, IconButton} from '@primer/react'
import {Tooltip} from '@primer/react/next'
import {useCallback, useEffect, useRef} from 'react'

import {ALIVE_REASONS} from '../../../constants/alive'
import {LABELS} from '../../../constants/labels'
import {useUpdateIssueSubscription} from '../../../mutations/use-update-issue-subscription'
import type {IssueSecondaryNode} from '../../issue-viewer-secondary-query-types'
import {type SubscriptionStatus, subscriptionStatusQueryKey} from './fetch-subscription-status'
import {useSubscriptionStatus} from './use-subscription-status'

// Plain fields formerly read via the leaf `StickySubscribeButtonFragment` (`id`, `threadSubscriptionChannel`)
// and the `StickySubscribeButtonRefetchableFragment` (`viewerThreadSubscriptionFormAction`, used to seed the
// TanStack query's initial subscription state).
export type StickySubscribeButtonFragment = Pick<
  IssueSecondaryNode,
  'id' | 'threadSubscriptionChannel' | 'viewerThreadSubscriptionFormAction'
>

export type StickySubscribeButtonProps = {
  subscriptionData: StickySubscribeButtonFragment
  owner: string
  repo: string
  number: number
  /**
   * Render variant. Defaults to `'bell'` (icon button used in the sticky header).
   * Use `'menu-item'` to render the action as an ActionList.Item inside an overflow menu.
   */
  variant?: 'bell' | 'menu-item'
}

export function StickySubscribeButton({
  subscriptionData,
  owner,
  repo,
  number,
  variant = 'bell',
}: StickySubscribeButtonProps) {
  const {threadSubscriptionChannel, id, viewerThreadSubscriptionFormAction} = subscriptionData

  if (!isLoggedIn() || !threadSubscriptionChannel) return null

  return (
    <StickySubscribeButtonInternal
      id={id}
      owner={owner}
      repo={repo}
      number={number}
      threadSubscriptionChannel={threadSubscriptionChannel}
      initialFormAction={viewerThreadSubscriptionFormAction ?? null}
      variant={variant}
    />
  )
}

function StickySubscribeButtonInternal({
  id,
  owner,
  repo,
  number,
  threadSubscriptionChannel,
  initialFormAction,
  variant,
}: {
  id: string
  owner: string
  repo: string
  number: number
  threadSubscriptionChannel: string
  initialFormAction: SubscriptionStatus['viewerThreadSubscriptionFormAction']
  variant: 'bell' | 'menu-item'
}) {
  const {updateSubscription} = useUpdateIssueSubscription({
    issueId: id,
    nameWithOwner: `${owner}/${repo}`,
    issueNumber: number,
  })
  const queryClient = useQueryClient()
  const shouldUpdateAnnouncement = useRef(false)

  const data = useSubscriptionStatus(owner, repo, number, {
    id,
    viewerThreadSubscriptionFormAction: initialFormAction,
    viewerCustomSubscriptionEvents: null,
  })
  const subscribed = data.viewerThreadSubscriptionFormAction === 'UNSUBSCRIBE'

  const announceSubscriptionUpdates = useCallback((newSubscribed: boolean, success = true) => {
    if (success) {
      announce(
        newSubscribed ? LABELS.notifications.subscribedAnnouncement : LABELS.notifications.unsubscribedAnnouncement,
      )
    } else {
      announce(LABELS.somethingWentWrong)
    }
  }, [])

  useEffect(() => {
    if (shouldUpdateAnnouncement.current) {
      announceSubscriptionUpdates(subscribed)
      shouldUpdateAnnouncement.current = false
    }
  }, [subscribed, announceSubscriptionUpdates])

  const refreshSubscription = useCallback(() => {
    shouldUpdateAnnouncement.current = true
    void queryClient.invalidateQueries({queryKey: subscriptionStatusQueryKey(owner, repo, number)})
  }, [queryClient, owner, repo, number])

  useAlive(threadSubscriptionChannel, (event: {reason: string}) => {
    if (!event?.reason) return
    if (event.reason === ALIVE_REASONS.SUBSCRIBED && !subscribed) {
      refreshSubscription()
    } else if (event.reason === ALIVE_REASONS.UNSUBSCRIBED && subscribed) {
      refreshSubscription()
    }
  })

  const toggleSubscription = useCallback(() => {
    const newSubscribed = !subscribed
    updateSubscription(
      {state: newSubscribed ? 'SUBSCRIBED' : 'UNSUBSCRIBED'},
      {
        onCompleted: () => announceSubscriptionUpdates(newSubscribed),
        onError: () => {
          announceSubscriptionUpdates(subscribed, false)
        },
      },
    )
  }, [updateSubscription, subscribed, announceSubscriptionUpdates])

  const menuItemLabel = subscribed ? LABELS.notifications.subscribedButton : LABELS.notifications.unsubscribedButton
  const tooltipText = subscribed
    ? `${LABELS.notifications.subscribedDescription} Click to unsubscribe`
    : `${LABELS.notifications.unsubscribedDescription} Click to subscribe`

  if (variant === 'menu-item') {
    return (
      <ActionList.Item onSelect={toggleSubscription}>
        <ActionList.LeadingVisual>{subscribed ? <BellIcon /> : <BellSlashIcon />}</ActionList.LeadingVisual>
        {menuItemLabel}
      </ActionList.Item>
    )
  }

  // Use a static aria-label with aria-pressed so screen readers announce the
  // toggle state once (e.g. "Notifications, toggle button, pressed") rather
  // than the confusing "Unsubscribe, toggle button, pressed" combination that
  // results from changing the label and the pressed state together.
  // See WAI-ARIA APG toggle button guidance.
  return (
    <Tooltip type="description" text={tooltipText} direction="s">
      <IconButton
        icon={subscribed ? BellIcon : BellSlashIcon}
        aria-label={LABELS.notifications.notificationsTitle}
        aria-pressed={subscribed}
        variant="invisible"
        onClick={toggleSubscription}
      />
    </Tooltip>
  )
}
